import { useState } from 'react';

const DISMISS_KEY = 'craftara_verify_banner_dismissed';

interface VerifyEmailBannerProps {
  email: string;
  onResend: () => Promise<boolean>;
  onRefresh: () => Promise<boolean>;
}

export default function VerifyEmailBanner({
  email,
  onResend,
  onRefresh,
}: VerifyEmailBannerProps) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  if (dismissed) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const resend = async () => {
    setBusy(true);
    setNote(null);
    try {
      const ok = await onResend();
      setNote(ok ? 'Verification email sent. Check your inbox.' : null);
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    setNote(null);
    try {
      const verified = await onRefresh();
      if (verified) {
        try {
          sessionStorage.removeItem(DISMISS_KEY);
        } catch {
          /* ignore */
        }
        setNote('Email confirmed. Thanks!');
      } else {
        setNote('Not verified yet. Open the link in your email, then try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="verify-email-banner" role="status">
      <div className="verify-email-banner__text">
        <strong>Confirm your email</strong>
        <span>
          We sent a link to <em>{email}</em>. You can keep using Craftara — confirm when you can.
        </span>
        {note && <span className="verify-email-banner__note">{note}</span>}
      </div>
      <div className="verify-email-banner__actions">
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void resend()}>
          Resend
        </button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void refresh()}>
          I&apos;ve verified
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={dismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
